/**
 * Count / status pill.
 *
 * Small enough to sit inside a sidebar row or a card header. `dot` drops the label and renders
 * the bare 8px indicator used for "there is something here" affordances.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX, ReactNode } from 'react';

/** Semantic color of the badge. */
export type BadgeTone = 'danger' | 'primary' | 'success' | 'neutral';

const TONE_STYLE: Record<BadgeTone, { background: string; color: string }> = {
  danger: { background: 'var(--color-danger)', color: 'var(--gl-white)' },
  primary: { background: 'var(--color-primary)', color: 'var(--gl-white)' },
  success: { background: 'var(--color-success)', color: 'var(--gl-white)' },
  neutral: { background: 'var(--color-border-strong)', color: 'var(--color-text)' },
};

export interface BadgeProps {
  children?: ReactNode;
  tone?: BadgeTone;
  /** Render as a bare 8px dot, ignoring `children`. */
  dot?: boolean;
  className?: string;
}

export const Badge = ({
  children,
  tone = 'danger',
  dot = false,
  className,
}: BadgeProps): JSX.Element => {
  const { background, color } = TONE_STYLE[tone];

  if (dot) {
    return (
      <span
        className={`${css`
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${background};
        `} ${className ?? ''}`}
      />
    );
  }

  return (
    <span
      className={`${css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: var(--radius-round);
        background: ${background};
        color: ${color};
        font-size: 10px;
        font-weight: var(--font-weight-bold);
        line-height: 1;
      `} ${className ?? ''}`}
    >
      {children}
    </span>
  );
};
